import React, { useState, useEffect, useContext } from "react";
import "../ContentPages/style.css";
import SubmitButton from "../Buttons/SubmitButton";
import BackButton from "../Buttons/BackButton";
import { useForm } from "react-hook-form";
import FieldError from "../FieldError";
import AgeWarningModal from "../Popups/AgeWarningModal"; // import your modal
import { redirectToRealMe } from "../../Services/apiService"; // import the redirect function
import { AppContext } from "../../contexts/AppContext";
import {
  initiateApplication,
  updateApplication,
  verifyArs,
} from "../../Services/applicationService"; // import the save function
import { useNavigate } from "react-router-dom";
import Loader from "../Loader/Loader";
import {
  getCountryByLabel,
  getValueFromLabel,
  removeAuthToken,
} from "../../Services/localStorage.tsx";
import { toTitleCase } from "../../Utils/stringHandler.tsx";
import DuplicateorStopWarningModel from "../Popups/IdentityExists/index.jsx";
import { calculateAge } from "../../Services/localStorage.tsx";
import "react-datepicker/dist/react-datepicker.css";
import FloatingDateField from "../FormFields/FloatingDateField.tsx";
import Calendar from "../../assets/images/Calendar.svg";
import FloatingTextField from "../FormFields/FloatingTextField.tsx";

interface PassportFormData {
  givenName: string;
  surname: string;
  dateOfBirth: string;
  passportNumber: string;
  genderId?: string;
  countryOfBirthId?: string;
  placeOfBirth?: string;
}

function checkPassport(issueDate, birthDate) {
  const [iy, im, id] = issueDate.split("-").map(Number);
  const [by, bm, bd] = birthDate.split("-").map(Number);

  // Condition 1: Issued before 2004
  if (iy < 2004) {
    return true;
  }

  // Calculate age at the time of issue
  let age = iy - by;
  if (im < bm || (im === bm && id < bd)) {
    age--; // hasn't had birthday yet
  }

  // Condition 2: Age less than 12
  if (age <= 12) {
    return true;
  }

  return false;
}

const Passport = ({ handleNext, handleBack }) => {
  const navigate = useNavigate();
  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors, isValid },
    reset,
  } = useForm<PassportFormData>({
    shouldFocusError: true,
    mode: "onTouched",
  });

  const { appState, setAppState, userFlows, setUserFlows } =
    useContext(AppContext);
  const [arsError, setArsError] = useState(false);
  const IVS_ADULT = "dee0075c-6dcd-ef11-b8e9-00224891c497";
  const IVS_CHILD = "fee0075c-6dcd-ef11-b8e9-00224891c497";

  const [showAgeModal, setShowAgeModal] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [pendingData, setPendingData] = useState<any>(null);
  const [duplicateData, setDuplicateData] = useState<any>(null);

  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);

  const surnameValue = watch("surname", "");
  const givenNameValue = watch("givenName", "");
  // const dateOfBirthValue = watch("dateOfBirth", "");

  // eslint-disable-next-line no-control-regex
  const containsSpecialChars = /[^\x00-\x7F]/g.test(surnameValue);
  // eslint-disable-next-line no-control-regex
  const containsSpecialCharsGivenName = /[^\x00-\x7F]/g.test(givenNameValue);
  const [showPicker, setShowPicker] = React.useState(false);
  const pickerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(event.target as Node)
      ) {
        setShowPicker(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const updateApp = (data: PassportFormData, applicationTypeId: string) => {
    setValidating(true);
    const updatedAppState = {
      ...appState,
      requestPayload: {
        ...appState.requestPayload,
        applicationTypeId: applicationTypeId,
        applicationFlowCode: 100000000,
        applicant: {
          ...appState.requestPayload.applicant,

          givenNames: toTitleCase(data.givenName),
          familyName: toTitleCase(data.surname),
          dateOfBirth: data.dateOfBirth,
          placeOfBirth: toTitleCase(data.placeOfBirth || ""),
          genderId: data.genderId,
          countryOfBirthId: data.countryOfBirthId,
          namesKnownAs: [
            {
              nameTypeId: "3ecbc107-c3b8-e911-a866-000d3ae10c61",
              familyName: toTitleCase(data.surname),
              givenNames: toTitleCase(data.givenName),
            },
          ],
        },
        eventTypeSearch: [
          {
            eventTypeId: "527c00ad-5756-ed11-9562-002248941c78",
            documentNumber: data.passportNumber,
            familyName: toTitleCase(data.surname || "").slice(0, 100),
            givenNames: toTitleCase(data.givenName || "").slice(0, 100),
            dateOfBirth: data.dateOfBirth,
            countryOfBirthId: data.countryOfBirthId,
            genderId: data.genderId,
          },
        ],
      },
    };
    // console.log("Updated Application State:", updatedAppState);
    if (updatedAppState.requestPayload.applicationId) {
      updateApplication(updatedAppState.requestPayload)
        .then((response) => {
          console.log("Application updated successfully:", response);
          setAppState(updatedAppState);
          handleNext();
          setValidating(false);
          setLoading(false);
        })
        .catch((error) => {
          console.error("Error initiating application:", error);
          setArsError(true);
          setValidating(false);
          setLoading(false);
        });
    } else
      initiateApplication(updatedAppState.requestPayload)
        .then((response) => {
          console.log("Application initiated successfully:", response);
          updatedAppState.requestPayload.applicationId = response.serviceId;
          setAppState(updatedAppState);
          handleNext();
          setValidating(false);
          setLoading(false);
        })
        .catch((error) => {
          console.error("Error initiating application:", error);
          setArsError(true);
          setValidating(false);
          setLoading(false);
        });
  };

  const extractAndTransformGenderFromResp = async (
    resp: any,
  ): Promise<string | null> => {
    // optional-chaining avoids “cannot read … of undefined”
    const genderLabel = resp?.person?.gender;
    if (!genderLabel) return null;

    // getValueFromLabel already returns a Promise<string>
    // so just await it (or return it directly).
    const genderId = await getValueFromLabel("GenderReference", genderLabel);
    return genderId ?? null;
  };
  useEffect(() => {
    try {
      const applicant = appState?.requestPayload?.applicant;
      const event = appState?.requestPayload?.eventTypeSearch?.[0];
      console.log("Applicant:", applicant);
      console.log("Event:", event);
      if (applicant || event) {
        reset({
          givenName: event?.givenNames || "",
          surname: event?.familyName || "",
          dateOfBirth: event?.dateOfBirth || "",
          passportNumber: event?.documentNumber || "",
        });
      }
    } catch (err) {
      console.error("Failed to load form data from localStorage:", err);
    }
  }, [
    reset,
    appState?.requestPayload?.applicant,
    appState?.requestPayload?.eventTypeSearch,
  ]);

  // const calculateAge = (dob) => {
  //   const birth = new Date(dob);
  //   const now = new Date();
  //   let age = now.getFullYear() - birth.getFullYear();
  //   const m = now.getMonth() - birth.getMonth();
  //   if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age--;
  //   return age;
  // };

  const onSubmit = (data) => {
    saveApplication(data, IVS_ADULT);
  };

  const saveApplication = async (
    data: PassportFormData,
    applicationTypeId: string,
    byPassVerifyArs: boolean = false,
    byPassDuplicate: boolean = false,
    byPassAge: boolean = false,
  ) => {
    setValidating(true);
    setArsError(false);
    console.log("Verifying ARS for data:", data);

    try {
      const vFormData = {
        arsId: data.passportNumber,
        register: "passport",
        givenName: data.givenName?.trim() === "" ? "xxx" : data.givenName,
        familyName: data.surname,
        dateOfBirth: data.dateOfBirth,
      };

      const resp = await verifyArs(vFormData, navigate);

      if (!resp) {
        throw new Error("No response from ARS");
      }

      if (
        !resp.ok &&
        resp.error === "Maximum identity verification attempts exceeded."
      ) {
        console.error("Maximum identity verification attempts exceeded.");
        removeAuthToken();
        navigate("/app/attempts-exhausted");
        return;
      }

      if (resp.error) {
        console.error("ARS verification failed:", resp);
        setArsError(true);
        setValidating(false);
        setLoading(false);
        return;
      }

      if (!byPassVerifyArs) {
        const age = calculateAge(data.dateOfBirth);
        if (!byPassAge && age < 14) {
          setValidating(false);
          setPendingData(data);
          setShowAgeModal(true);
          setUserFlows({
            ...userFlows,
            showLiveness: false,
            childApplication: true,
          });

          return;
        }

        if (
          checkPassport(resp?.person?.dateIssued, resp?.person?.date_of_birth)
        ) {
          setUserFlows((prev: any) => ({
            ...prev,
            showRefree: prev.isRenew ? false : true,
          }));
        }

        console.log("ARS verification successful:", resp);
        if (
          resp?.person?.dateIssued &&
          new Date(resp?.person?.dateIssued) < new Date("2004-01-01")
        ) {
          setUserFlows((prev: any) => ({
            ...prev,
            showRefree: prev.isRenew ? false : true,
          }));
        }

        // Check for duplicate or stop flags
        if (resp?.person?.stop) {
          // TODO: See how this should be handled (before same as duplicate)
          console.log("Stop found in ARS response");
          setPendingData(data);
          setDuplicateData(!!resp?.person?.duplicate);
          setValidating(false);
          setShowDuplicateModal(true);
          setLoading(false);
          return;
        }
        if (resp?.person?.duplicate && !byPassDuplicate) {
          console.log("Duplicate found in ARS response");
          setPendingData(data);
          setDuplicateData(!!resp?.person?.duplicate);
          setValidating(false);
          setShowDuplicateModal(true);
          setLoading(false);
          return;
        }
      }

      // Populate additional data
      const genderId =
        (await extractAndTransformGenderFromResp(resp)) || undefined;
      const countryOfBirthId = await getCountryByLabel(
        resp?.person?.country_of_birth,
      );

      data.genderId = genderId;
      data.countryOfBirthId =
        countryOfBirthId?.dia_countryid ||
        "18ebb77f-2341-e911-a85e-000d3ae0eb6e";
      data.placeOfBirth = toTitleCase(
        resp?.person?.place_of_birth?.trim() || "Unknown",
      );

      // Update app and clean up
      updateApp(data, applicationTypeId);
      setArsError(false);
    } catch (error) {
      console.error("Error verifying ARS:", error);
      setArsError(true);
      setLoading(false);
    } finally {
    }
  };

  const handleAgeModalContinue = () => {
    setShowAgeModal(false);
    // Double-check age as pendingData is set also for duplicate/stop
    if (pendingData && calculateAge(pendingData.dateOfBirth) < 14) {
      saveApplication(pendingData, IVS_CHILD, false, false, true);
    }
  };

  const handleDuplicateModalContinue = () => {
    setShowDuplicateModal(false);
    if (pendingData) {
      if (calculateAge(pendingData.dateOfBirth) < 14) {
        // If child, agemodal was displayed before and continued, so bypass it.
        saveApplication(pendingData, IVS_CHILD, false, true, true);
      } else {
        saveApplication(pendingData, IVS_ADULT, false, true);
      }
    }
  };

  const handleModalCancel = () => {
    redirectToRealMe();
  };

  const onClose = () => {
    setShowAgeModal(false);
    setShowDuplicateModal(false);
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      {validating && <Loader />}
      {showAgeModal && (
        <AgeWarningModal
          onContinue={handleAgeModalContinue}
          onCancel={handleModalCancel}
          onClose={onClose}
        />
      )}

      {showDuplicateModal && (
        <DuplicateorStopWarningModel
          onContinue={handleDuplicateModalContinue}
          onCancel={handleModalCancel}
          onClose={onClose}
          showDuplicate={duplicateData}
        />
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="intro">
          <h1 className="intro-title-mi" lang="mi-NZ">
            Kōrero mai ko wai koe
          </h1>
          <hr />
          <h1 className="intro-title">Tell us who you are</h1>

          <div className="step-container">
            <h2 className="step-title mb-2">
              Your New Zealand passport details
            </h2>
            <p className="mb-4">
              Enter the details exactly as they appear on your passport.
            </p>

            <FloatingTextField
              name="givenName"
              label="Given name(s)"
              inputProps={register("givenName", {
                validate: (value) => {
                  return (
                    value?.trim().length < 198 ||
                    "Your given name must be fewer than 198 characters."
                  );
                },
              })}
              error={errors.givenName}
            />
            <p
              style={{
                fontFamily: "Inter",
                fontSize: "14px",
                fontWeight: 400,
              }}
            >
              If you have one name, enter it as your surname or family name.
            </p>

            {/* <FieldError error={errors.givenName} info={null} /> */}

            <FloatingTextField
              name="surname"
              label="Surname or family name"
              required
              inputProps={register("surname", {
                required:
                  "If you are entering one name, please enter it in surname or family name.",
                maxLength: {
                  value: 84,
                  message:
                    "Your surname or family name must be fewer than 85 characters.",
                },
              })}
              error={errors.surname}
            />
            {/* <FieldError error={errors.surname} info={null} /> */}

            {(containsSpecialChars || containsSpecialCharsGivenName) && (
              <FieldError
                error={null}
                info="Your name contains a special character (such as an umlaut, cedilla, accent, or macron). When your name is recorded by other organisations that use RealMe, it may appear differently."
              />
            )}

            <FloatingTextField
              name="passportNumber"
              label="Passport number"
              required
              inputProps={register("passportNumber", {
                required: "This field is required",
                pattern: {
                  value: /^[A-Za-z]{1,2}\d{6}$/,
                  message: "Invalid NZ passport number format",
                },
              })}
              error={errors.passportNumber}
            />
            {/* <FieldError error={errors.passportNumber} info={null} /> */}

            <FloatingDateField
              name="dateOfBirth"
              label="Date of birth (dd/mm/yyyy)"
              control={control}
              calendarIcon={Calendar}
              error={errors.dateOfBirth}
              required
            />
            {arsError && (
              <FieldError
                info={null}
                error={{
                  message:
                    "We could not verify your passport details. Please check your details and try again.",
                }}
              />
            )}
          </div>
        </div>

        <div className="step-button-container">
          <SubmitButton
            onClick={() => {}}
            text={"Save and continue"}
            type="submit"
            disabled={!isValid}
          />
          <BackButton text={"Back"} onClick={handleBack} />
        </div>
      </form>
    </>
  );
};

export default Passport;
